import axiosInstance from "api/axiosInstance";
import tenantAxiosInstance from "api/tenantAxiosInstance.ts";
import { sessionAtom, SessionState } from "atoms/sessionAtom.ts";
import { useEffect } from "react";
import { useSetRecoilState } from "recoil";
import { LocalStorageKey } from "types/localStorage.ts";

import UserData = App.Data.UserData;

export const getSessionFromStorage = (): SessionState => {
    const session = localStorage.getItem(LocalStorageKey.Session);
    return session ? JSON.parse(session) : { token: null, user: null, activeTenantSlug: null };
};

export const setSessionInStorage = (session: SessionState) => {
    localStorage.setItem(LocalStorageKey.Session, JSON.stringify(session));
};

export const removeSessionFromStorage = () => {
    localStorage.removeItem(LocalStorageKey.Session);
};

export const removeSession = () => {
    removeSessionFromStorage();
    axiosInstance.setAuthorization(null);
    tenantAxiosInstance.setAuthorization(null);
};

export const useSession = () => {
    const setSessionState = useSetRecoilState(sessionAtom);

    useEffect(() => {
        const currentSession = getSessionFromStorage();
        updateSessionState(currentSession);
    }, []);

    const updateSessionState = (newState: Partial<SessionState>) => {
        const currentSession = getSessionFromStorage();
        const updatedSession = { ...currentSession, ...newState };
        setSessionInStorage(updatedSession);
        if (newState.token !== undefined) {
            axiosInstance.setAuthorization(newState.token);
            tenantAxiosInstance.setAuthorization(newState.token);
        }
        setSessionState(updatedSession);
    };

    const getTokenFromStorage = (): string | null => {
        return getSessionFromStorage().token;
    };

    const setToken = (token: string | null) => {
        updateSessionState({ token });
    };

    const setUser = (user: UserData) => {
        updateSessionState({ user });
    };

    const removeToken = () => {
        updateSessionState({ token: null });
    };

    const removeUser = () => {
        updateSessionState({ user: null });
    };

    return {
        getTokenFromStorage,
        setToken,
        setUser,
        removeToken,
        removeUser,
        removeSession,
    };
};
